/**
 * Drafts Page
 * Allows users to view, edit, and publish their draft blog posts
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import { useSafeThemeClasses } from '@/shared/theme';
import { useMediaQuery } from '@/shared/hooks';
import { useFetchBlogs } from '@/features/blog';
import { usePublishBlog } from '@/features/blog/operations/usePublishBlog';
import { useDeleteBlog } from '@/features/blog/operations/useDeleteBlog';
import { ROUTES } from '@/shared/config';
import { Button, Alert, useConfirmDialog } from '@/shared/components/ui';
import { logger } from '@/shared/utils';
import toast from 'react-hot-toast';
import type { BlogPost } from '@/features/blog/api';

const DraftsPage: React.FC = () => {
	const theme = useSafeThemeClasses();
	const navigate = useNavigate();
	// Desktop-only permanent rail - hidden entirely below lg: (mobile uses the
	// tab row instead, so there's no off-canvas drawer/second hamburger
	// stacked under the global nav's own hamburger). Tailwind v4 + DaisyUI's
	// compiled CSS doesn't reliably let translate-x utility classes win over
	// each other for the standalone `translate` property here, so the
	// transform is still driven inline rather than via classes.
	const isDesktop = useMediaQuery('(min-width: 1024px)');
	const { fetchBlogs, blogs, loading, error } = useFetchBlogs();
	const { publishBlog, loading: publishLoading } = usePublishBlog();
	const { deleteBlog, loading: deleteLoading } = useDeleteBlog();
	const { confirm, ConfirmDialog } = useConfirmDialog();
	
	const [drafts, setDrafts] = useState<BlogPost[]>([]);
	const [filteredDrafts, setFilteredDrafts] = useState<BlogPost[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [deletingId, setDeletingId] = useState<string | null>(null);

	// Fetch user's drafts (unpublished blogs)
	useEffect(() => {
		const loadDrafts = async () => {
			try {
				// Fetch all blogs with is_published=false filter
				await fetchBlogs({ is_published: false, limit: 1000 });
			} catch (err) {
				logger.error('Failed to load drafts', 'DraftsPage', { error: err });
				toast.error('Failed to load drafts');
			}
		};

		loadDrafts();
	}, [fetchBlogs]);

	// Filter drafts based on search term
	useEffect(() => {
		if (!searchTerm.trim()) {
			setFilteredDrafts(drafts);
			return;
		}

		const filtered = drafts.filter(
			(draft) =>
				draft.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
				draft.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				draft.tags?.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()))
		);
		setFilteredDrafts(filtered);
	}, [drafts, searchTerm]);

	// Update drafts when blogs change
	useEffect(() => {
		const userDrafts = blogs.filter((blog) => !blog.is_published);
		setDrafts(userDrafts);
		setFilteredDrafts(userDrafts);
	}, [blogs]);

	const handleEdit = (draftId: string) => {
		navigate(ROUTES.BLOG_EDIT(draftId));
	};

	const handlePreview = (draftId: string) => {
		navigate(ROUTES.BLOG_PREVIEW(draftId));
	};

	const handleTileKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, draftId: string) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			handlePreview(draftId);
		}
	};

	const handlePublish = async (draftId: string) => {
		try {
			const success = await publishBlog(draftId);
			if (success) {
				toast.success('Blog published successfully!');
				// Refresh drafts list
				await fetchBlogs({ is_published: false, limit: 1000 });
			} else {
				toast.error('Failed to publish blog');
			}
		} catch (err) {
			logger.error('Failed to publish draft', 'DraftsPage', { error: err, draftId });
			toast.error('Failed to publish blog');
		}
	};

	const handleDelete = async (draftId: string) => {
		const ok = await confirm({
			title: 'Delete draft?',
			message: 'This permanently deletes the draft and cannot be undone.',
			confirmLabel: 'Delete',
			danger: true,
			requireTypedWord: 'DELETE',
		});
		if (!ok) {
			return;
		}

		try {
			setDeletingId(draftId);
			const success = await deleteBlog(draftId);
			if (success) {
				toast.success('Draft deleted successfully');
				// Refresh drafts list
				await fetchBlogs({ is_published: false, limit: 1000 });
			} else {
				toast.error('Failed to delete draft');
			}
		} catch (err) {
			logger.error('Failed to delete draft', 'DraftsPage', { error: err, draftId });
			toast.error('Failed to delete draft');
		} finally {
			setDeletingId(null);
		}
	};

	if (loading) {
		return (
			<div className={`min-h-screen flex items-center justify-center ${theme.background}`}>
				<div className={`${theme.card} rounded-2xl p-8`}>
					<div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme.textAccent} mx-auto mb-4`}></div>
					<p className={theme.textSecondary}>Loading drafts...</p>
				</div>
			</div>
		);
	}

	return (
		<div className={`flex min-h-screen transition-opacity duration-500 ${theme.background} pb-20`}>
			<ConfirmDialog />
			{/* Background decoration */}
			<div className='fixed inset-0 overflow-hidden pointer-events-none'>
				<div className={`absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[0]} animate-pulse`}></div>
				<div className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[1]} animate-pulse`} style={{ animationDelay: '1s' }}></div>
			</div>

			{/* Sidebar is portaled to <body> - position:fixed breaks out of the
				viewport when nested inside a backdrop-blur ancestor (theme.background
				applies backdrop-filter, which establishes a new CSS containing block
				for fixed descendants per spec), which was pushing the sidebar fully
				off-screen. It's desktop-only (lg:); on mobile, navigation happens via
				the tab row below instead of a second hamburger-triggered drawer, so
				there's only ever one menu control on screen (the global nav's) at
				once. */}
			{createPortal(
			<div
				className={`fixed top-[4.8rem] left-0 h-[calc(100%-4.8rem)] w-64 border-r z-20 transition-all duration-300 ease-in-out ${theme.sidebar}`}
				style={{ transform: isDesktop ? 'translateX(0)' : 'translateX(-100%)' }}
			>
				{/* Header */}
				<div className={`p-5 flex justify-between items-center border-b ${theme.separator}`}>
					<h2 className={`font-bold text-xl ${theme.textHeading} tracking-tight`}>Drafts</h2>
				</div>

				{/* Navigation Menu */}
				<nav className="p-4 space-y-1.5">
					{/* Back to Dashboard */}
					<Link
						to={ROUTES.DASHBOARD}
						className={`w-full flex items-center p-3 rounded-xl transition-all duration-300 group ${theme.navItem} ${theme.textPrimary} hover:bg-opacity-50 hover:scale-[1.01]`}
					>
						<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
						</svg>
						<span className={`ml-3 font-medium transition-opacity duration-300`}>Dashboard</span>
					</Link>

					{/* Blog */}
					<Link
						to={ROUTES.BLOG}
						className={`w-full flex items-center p-3 rounded-xl transition-all duration-300 group ${theme.navItem} ${theme.textPrimary} hover:bg-opacity-50 hover:scale-[1.01]`}
					>
						<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
						</svg>
						<span className={`ml-3 font-medium transition-opacity duration-300`}>Blog</span>
					</Link>

					{/* Create New Draft */}
					<button
						onClick={() => navigate(ROUTES.BLOG_CREATE)}
						className={`w-full flex items-center p-3 rounded-xl transition-all duration-300 group ${theme.navItemActive} shadow-lg scale-[1.02]`}
					>
						<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
						</svg>
						<span className={`ml-3 font-medium transition-opacity duration-300`}>New Draft</span>
					</button>
				</nav>
			</div>,
					document.body
				)}

			{/* Main Content Area - sidebar is permanently visible from lg: up */}
			<div className="relative flex-1 transition-all duration-300 ml-0 lg:ml-64">
				{/* Top Bar - Page title (no toggle button: the global nav already owns
					the one hamburger on this page; mobile navigation happens via the tab
					row directly below) */}
				<div className={`sticky top-0 z-10 ${theme.header}`}>
					<div className="px-6 py-4">
						<h1 className={`text-2xl font-bold text-center ${theme.textHeading} tracking-tight`}>My Drafts</h1>
					</div>

					{/* Mobile section tabs - replaces the old off-canvas drawer on small
						screens so there's a single, always-visible way to navigate
						instead of a redundant second hamburger. */}
					<nav className="lg:hidden flex gap-2 overflow-x-auto px-4 pb-3 -mt-1">
						<Link
							to={ROUTES.DASHBOARD}
							className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${theme.buttonGhost} ${theme.textSecondary}`}
						>
							Dashboard
						</Link>
						<Link
							to={ROUTES.BLOG}
							className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${theme.buttonGhost} ${theme.textSecondary}`}
						>
							Blog
						</Link>
						<button
							onClick={() => navigate(ROUTES.BLOG_CREATE)}
							className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${theme.buttonPrimary} ${theme.buttonTextPrimary}`}
						>
							New Draft
						</button>
					</nav>
				</div>

				{/* Content - Elevated Glass Tile */}
				<div className="p-6 md:p-8 lg:p-10 min-h-[calc(100vh-120px)]">
					<div className="max-w-6xl mx-auto">
						{/* Header Section */}
						<div className="mb-8">
							<div className="flex items-center justify-between mb-4">
								<div>
									<p className={`text-sm ${theme.textSecondary} mb-2`}>
										Manage and publish your draft blog posts
									</p>
								</div>
								<Button
									onClick={() => navigate(ROUTES.BLOG_CREATE)}
									className="flex items-center gap-2">
									<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
									</svg>
									New Draft
								</Button>
							</div>

							{/* Search */}
							<div className="max-w-md">
								<input
									type="text"
									placeholder="Search drafts..."
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									className={`w-full px-4 py-2.5 ${theme.input} rounded-xl ${theme.textPrimary} ${theme.textPlaceholder} focus:outline-none focus:ring-2 focus:scale-[1.01] transition-all duration-300 ${theme.textAccent}`}
								/>
							</div>
						</div>

						{/* Error Message */}
						{error && (
							<div className="mb-6">
								<Alert type="error" message={error} />
							</div>
						)}

						{/* Drafts List */}
						{filteredDrafts.length === 0 ? (
							<div className={`rounded-2xl p-12 text-center transition-all duration-300 hover:shadow-2xl ${theme.card}`}>
								<svg
									className={`w-16 h-16 mx-auto mb-4 ${theme.textMuted}`}
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
									/>
								</svg>
								<h3 className={`text-xl font-semibold mb-2 ${theme.textHeading}`}>
									{searchTerm ? 'No drafts found' : 'No drafts yet'}
								</h3>
								<p className={`mb-6 ${theme.textSecondary}`}>
									{searchTerm
										? 'Try adjusting your search terms'
										: 'Start writing your first blog post'}
								</p>
								{!searchTerm && (
									<Button onClick={() => navigate(ROUTES.BLOG_CREATE)}>
										Create Your First Draft
									</Button>
								)}
							</div>
						) : (
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
								{filteredDrafts.map((draft) => (
									<div
										key={draft.id}
										role="button"
										tabIndex={0}
										onClick={() => handlePreview(draft.id)}
										onKeyDown={(event) => handleTileKeyDown(event, draft.id)}
										className={`relative overflow-hidden rounded-2xl p-4 pb-8 transition-all duration-300 hover:shadow-2xl cursor-pointer focus:outline-none focus:ring-2 ${theme.textAccent} ${theme.card}`}>
										<button
											type="button"
											onClick={(event) => {
												event.stopPropagation();
												handleDelete(draft.id);
											}}
											disabled={publishLoading || deleteLoading || deletingId === draft.id}
											className={`absolute top-4 right-4 z-10 h-9 w-9 rounded-full inline-flex items-center justify-center transition-all duration-200 ${theme.buttonGhost} ${theme.textError} hover:${theme.alertError} active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed`}
											title="Delete draft"
											aria-label={`Delete ${draft.title || 'draft'}`}
										>
											{deletingId === draft.id ? (
												<div className={`animate-spin rounded-full h-4 w-4 border-b-2 ${theme.textError}`}></div>
											) : (
												<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
												</svg>
											)}
										</button>
										{/* Featured Image */}
										{draft.featured_image_url && (
											<div className="w-full h-40 mb-4 overflow-hidden rounded-lg">
												<img
													src={draft.featured_image_url}
													alt={draft.title}
													className="w-full h-full object-cover"
												/>
											</div>
										)}

										{/* Title */}
										<h3 className={`text-lg font-semibold mb-2 ${theme.textHeading} line-clamp-2`}>
											{draft.title || 'Untitled Draft'}
										</h3>

										{/* Excerpt */}
										{draft.excerpt && (
											<p className={`text-sm mb-4 ${theme.textSecondary} line-clamp-3`}>
												{draft.excerpt}
											</p>
										)}

										{/* Tags */}
										{draft.tags && draft.tags.length > 0 && (
											<div className="flex flex-wrap gap-1 mb-4">
												{draft.tags.slice(0, 3).map((tag, index) => (
													<span
														key={index}
														className={`text-xs px-2 py-1 ${theme.badge} ${theme.textSecondary} rounded-md`}>
														{tag}
													</span>
												))}
												{draft.tags.length > 3 && (
													<span className={`text-xs px-2 py-1 ${theme.textSecondary}`}>
														+{draft.tags.length - 3}
													</span>
												)}
											</div>
										)}

										{/* Last Updated */}
										{draft.updated_at && (
											<p className={`text-xs mb-4 ${theme.textMuted}`}>
												Last updated: {new Date(draft.updated_at).toLocaleDateString()}
											</p>
										)}

										{/* Actions */}
										<div className={`relative z-6 grid grid-cols-2 gap-6 mt-5 pt-6 border-t ${theme.separator}`}>
											<button
												onClick={(event) => {
													event.stopPropagation();
													handleEdit(draft.id);
												}}
												className={`w-full h-9 px-3 rounded-lg transition-all duration-200 text-sm font-medium inline-flex items-center justify-center gap-2 ${theme.buttonSecondary} active:scale-[0.88]`}
												disabled={publishLoading || deleteLoading}>
												<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
												</svg>
												<span>Edit</span>
											</button>
											<button
												onClick={(event) => {
													event.stopPropagation();
													handlePublish(draft.id);
												}}
												disabled={publishLoading || deleteLoading}
												className={`w-full h-9 px-3 rounded-lg transition-all duration-200 text-sm font-medium inline-flex items-center justify-center gap-2 ${theme.buttonPrimary} active:scale-[0.88] disabled:opacity-50 disabled:cursor-not-allowed`}>
												{publishLoading ? (
													<>
														<div className={`animate-spin rounded-full h-4 w-4 shrink-0 border-b-2 ${theme.textPrimary}`}></div>
														<span>Publishing...</span>
													</>
												) : (
													<>
														<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
														</svg>
														<span>Publish</span>
													</>
												)}
											</button>
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default DraftsPage;

